import React, { useState, useEffect } from "react";
import { ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { useSupervisor } from "../../../contexts/SupervisorContext";
import { useAccount } from "../../../contexts/AccountContext";
import { storagePaths } from "@visitproof/types";

interface Props {
  supervisorId: string;
}

const UploadFileButton: React.FC<Props> = ({ supervisorId }) => {
  const { addFileToSupervisor } = useSupervisor();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (file && !name) {
      setName(file.name);
    }
  }, [file, name]);

  const handleSubmit = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);

    try {
      const path = account?.id
        ? storagePaths.supervisorFile(account.id, supervisorId, file.name)
        : `supervisors/${supervisorId}/${file.name}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);
      await addFileToSupervisor(supervisorId, {
        path: fileRef.fullPath,
        name,
        description,
        isVisibleToSupervisor: false,
      });
      clearForm();
    } catch (error) {
      alert("Failed to upload file. Please try again later.");
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setFile(null);
    setName("");
    setDescription("");
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create File
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Upload File</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>File</Label>
                <Input
                  type="file"
                  onChange={(e) =>
                    setFile(e.target.files ? e.target.files[0] : null)
                  }
                />
              </Field>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  placeholder="Name"
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  placeholder="Description"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={!file || isUploading} onClick={handleSubmit}>
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadFileButton;
